/*
 *  Copyright (c) 2015 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */

import { info, log, error } from "../modules/logger";
import { createUUID, getValueFromSetting } from "../modules/helper";
import { getTURNCredentials, TEST_TYPE } from "../modules/rtc";
import { ACTIONS } from "../actions/constants";
import { startAnalyze, stopAnalyze } from "./callmetrics";

const moduleName = "mod:p2p";

let localStream;
let pc1;
let pc2;
let localVideo;
let remoteVideo;
let iceSettings;
let instanceId;
let startTime;
let dispatcher;
let gotCandidate = false;

const offerOptions = {
  offerToReceiveAudio: 1,
  offerToReceiveVideo: 1,
};

function getName(pc) {
  return pc === pc1 ? "pc1" : "pc2";
}

function getOtherPc(pc) {
  return pc === pc1 ? pc2 : pc1;
}

function isLocal(pc) {
  return pc === pc1;
}

function getConfiguration(name, testType, limitedUrl) {
  const getTurnServersUrl = (settings, instanceId, credentials) => {
    const TURNSettingURL = getValueFromSetting(
      "turnAddress",
      settings,
      instanceId,
    );
    if (!TURNSettingURL) {
      return [];
    }
    if (TURNSettingURL) {
      const TURNServers = TURNSettingURL.replace(" ", "")
        .trim()
        .split(";")
        .filter((url) => url.length > 0);
      const iceServers = TURNServers.map((url) => {
        if (!url.startsWith("turn:") && !url.startsWith("turns:")) {
          url = `turn:${url}`;
        }
        return {
          urls: url,
          username: credentials.username,
          credential: credentials.password,
        };
      });
      return iceServers;
    }
  };

  const getStunServersUrl = (settings, instanceId) => {
    const STUNSettingURL = getValueFromSetting(
      "stunAddress",
      settings,
      instanceId,
    );
    if (!STUNSettingURL) {
      return [];
    }
    const STUNServers = STUNSettingURL.replace(" ", "")
      .trim()
      .split(";")
      .filter((url) => url.length > 0);
    const iceServers = STUNServers.map((url) => {
      if (!url.startsWith("stun:")) {
        url = `stun:${url}`;
      }
      return {
        urls: url,
      };
    });
    return iceServers;
  };

  const credentials = getTURNCredentials(
    name,
    getValueFromSetting("turnUsername", iceSettings, instanceId),
    getValueFromSetting("turnPassword", iceSettings, instanceId),
    getValueFromSetting("turnToken", iceSettings, instanceId),
  );

  if (testType === TEST_TYPE.UNRESTRICTED) {
    let iceServers = getStunServersUrl(iceSettings, instanceId);
    iceServers = iceServers.concat(
      getTurnServersUrl(iceSettings, instanceId, credentials),
    );

    return {
      iceServers,
      iceTransportPolicy: "all",
    };
  }

  if (testType === TEST_TYPE.STUN) {
    let iceServers = getStunServersUrl(iceSettings, instanceId);
    return {
      iceServers,
    };
  }

  if (testType === TEST_TYPE.HOST) {
    return {
      iceServers: [],
      iceTransportPolicy: "all",
    };
  }

  return {
    iceServers: [
      {
        urls: limitedUrl,
        username: credentials.username,
        credential: credentials.password,
      },
    ],
    iceTransportPolicy: "relay",
  };
}

const onResize = () => {
  log(moduleName, "remote video resized", {
    videoWidth: remoteVideo.videoWidth,
    videoHeight: remoteVideo.videoHeight,
  });
  // We'll use the first onsize callback as an indication that video has started
  // playing out.
  if (startTime) {
    const elapsedTime = Math.floor(window.performance.now() - startTime);
    log(moduleName, "setup time", { time: `${elapsedTime.toFixed(3)}ms` });
    startTime = null;
    dispatcher({
      type: ACTIONS.VIDEO_SUCCESS,
      payload: { value: { elapsedTime } },
    });
  }
};

function listenToVideoEvent() {
  if (localVideo) {
    localVideo.addEventListener("loadedmetadata", function () {
      log(moduleName, "Local video metadata", {
        videoWidth: this.videoWidth,
        videoHeight: this.videoHeight,
      });
    });
  }

  remoteVideo.addEventListener("loadedmetadata", function () {
    log(moduleName, "remote video metadata", {
      videoWidth: this.videoWidth,
      videoHeight: this.videoHeight,
    });
  });

  remoteVideo.addEventListener("resize", onResize);
}

export const initialize = async (
  local,
  remote,
  settings,
  currentInstanceId,
) => {
  return new Promise((resolve, reject) => {
    localVideo = local;
    remoteVideo = remote;
    iceSettings = settings;
    instanceId = currentInstanceId;
    listenToVideoEvent();
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: true })
      .then((stream) => {
        localVideo.srcObject = stream;
        localStream = stream;
        resolve(stream);
      })
      .catch((e) => {
        error(moduleName, "error getting media", { e });
        reject(null);
      });
  });
};

const endCall = async () => {
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }
  if (localVideo) {
    localVideo.src = null;
  }
  if (remoteVideo) {
    remoteVideo.removeEventListener("resize", onResize);
    remoteVideo.src = null;
  }

  // Stop analyze
  stopAnalyze();
};

export const call = async (testType, limitedUrl, dispatch) => {
  dispatcher = dispatch;
  return new Promise(async (resolve, reject) => {
    info(moduleName, "start call");
    try {
      startTime = window.performance.now();
      const videoTracks = localStream.getVideoTracks();
      const audioTracks = localStream.getAudioTracks();
      if (videoTracks.length > 0) {
        info(moduleName, "Using video device", { video: videoTracks[0] });
      }
      if (audioTracks.length > 0) {
        info(moduleName, "Using audio device", { track: audioTracks[0] });
      }

      const callId = createUUID();

      const config1 = getConfiguration(
        `tcpeer-${createUUID()}#${callId}`,
        testType,
        limitedUrl,
      );
      const config2 = getConfiguration(
        `tcpeer-${createUUID()}#${callId}`,
        testType,
        limitedUrl,
      );

      info(moduleName, "PC1 configuration", config1);
      info(moduleName, "PC2 configuration", config2);

      pc1 = new RTCPeerConnection(config1);
      pc1.addEventListener("icecandidate", (e) =>
        onIceCandidate(pc1, testType, e),
      );
      pc2 = new RTCPeerConnection(config2);
      pc2.addEventListener("icecandidate", (e) =>
        onIceCandidate(pc2, testType, e),
      );
      pc1.addEventListener("iceconnectionstatechange", (e) => {
        onIceStateChange(pc1, e);
      });
      pc2.addEventListener("iceconnectionstatechange", (e) => {
        onIceStateChange(pc2, e);
      });
      pc2.addEventListener("track", gotRemoteStream);

      localStream
        .getTracks()
        .forEach((track) => pc1.addTrack(track, localStream));

      const offer = await pc1.createOffer(offerOptions);
      await onCreateOfferSuccess(offer);
      resolve();
    } catch (e) {
      onCreateSessionDescriptionError(e);
      reject();
    }
  });
};

function onCreateSessionDescriptionError(err) {
  error(moduleName, "error in create session", { err });
}

async function onCreateOfferSuccess(desc) {
  try {
    await pc1.setLocalDescription(desc);
    onSetLocalSuccess(pc1);
  } catch (e) {
    onSetSessionDescriptionError();
  }

  try {
    await pc2.setRemoteDescription(desc);
    onSetRemoteSuccess(pc2);
  } catch (e) {
    onSetSessionDescriptionError();
  }

  // Since the 'remote' side has no media stream we need
  // to pass in the right constraints in order for it to
  // accept the incoming offer of audio and video.
  try {
    //localStream.getTracks().forEach(track => pc2.addTrack(track, localStream));
    const answer = await pc2.createAnswer();
    await onCreateAnswerSuccess(answer);
  } catch (e) {
    onCreateSessionDescriptionError(e);
  }
}

function onSetLocalSuccess(pc) {
  info(moduleName, "set local description complete", { pc: getName(pc) });
}

function onSetRemoteSuccess(pc) {
  info(moduleName, "set remote description complete", { pc: getName(pc) });
}

function onSetSessionDescriptionError(error) {
  error(moduleName, "error in session description", { error });
}

function gotRemoteStream(e) {
  if (remoteVideo.srcObject !== e.streams[0]) {
    remoteVideo.srcObject = e.streams[0];
  }
}

async function onCreateAnswerSuccess(desc) {
  try {
    await pc2.setLocalDescription(desc);
    onSetLocalSuccess(pc2);
  } catch (e) {
    onSetSessionDescriptionError(e);
  }
  try {
    await pc1.setRemoteDescription(desc);
    onSetRemoteSuccess(pc1);
  } catch (e) {
    onSetSessionDescriptionError(e);
  }
}

async function onIceCandidate(pc, testType, event) {
  if (!event.candidate) {
    if (gotCandidate) {
      dispatcher({
        type: ACTIONS.CALL_CANDIDATE_SUCCESS,
        payload: { value: null },
      });
    } else {
      dispatcher({
        type: ACTIONS.CALL_CANDIDATE_FAILED,
        payload: { value: null },
      });
    }
  } else if (
    event.candidate &&
    event.candidate.type === "relay" &&
    getName(pc) === "pc1"
  ) {
    gotCandidate = true;
  } else if (
    event.candidate &&
    event.candidate.type === "host" &&
    (testType === TEST_TYPE.STUN || testType === TEST_TYPE.HOST) &&
    getName(pc) === "pc1"
  ) {
    gotCandidate = true;
  } else if (
    event.candidate &&
    event.candidate.type === "srflx" &&
    getName(pc) === "pc1"
  ) {
    dispatcher({
      type: ACTIONS.VIDEO_ADDRESS,
      payload: { value: { address: event.candidate.address } },
    });
  }
  try {
    if (event.candidate) {
      //&& event.candidate.type === "relay") {
      await getOtherPc(pc).addIceCandidate(event.candidate);
      onAddIceCandidateSuccess(pc);
    }
  } catch (e) {
    onAddIceCandidateError(pc, e);
  }
  if (event.candidate) {
    log(moduleName, "on ICE candidate", {
      pc: getName(pc),
      type: event.candidate ? event.candidate.type : "null",
      ice: event.candidate,
    });
  }
}

function onAddIceCandidateSuccess(pc) {
  info(moduleName, "add candidate success", { pc: getName(pc) });
}

function onAddIceCandidateError(pc, err) {
  error(moduleName, "error to add ICE candidate", { pc: getName(pc) });
}

function onIceStateChange(pc, event) {
  if (pc) {
    log(moduleName, "ICE state changed", {
      pc: getName(pc),
      ICEState: pc.iceConnectionState,
      connectionState: pc.connectionState,
      signalingState: pc.signalingState,
    });
    if (getName(pc) === "pc1") {
      if (pc.iceConnectionState === "connected") {
        dispatcher({
          type: ACTIONS.CALL_ICE_SUCCESS,
          payload: { value: null },
        });

        //Start analyzing
        startAnalyze(
          pc1,
          "pc1",
          "call_test",
          "uid_sender",
          pc2,
          "pc2",
          "call_test",
          "uid_receiver",
          dispatcher,
        );
      } else if (pc.iceConnectionState === "failed") {
        dispatcher({
          type: ACTIONS.CALL_ICE_FAILED,
          payload: { value: null },
        });
      } else if (pc.iceConnectionState === "disconnected") {
        dispatcher({
          type: ACTIONS.CALL_ICE_FAILED,
          payload: { value: null },
        });
      }
    }
  }
}

export const hangup = () => {
  info(moduleName, "ending call", {});
  endCall();
  if (pc1) {
    pc1.close();
    pc1 = null;
  }
  if (pc2) {
    pc2.close();
    pc2 = null;
  }
};
